import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Zap, ChevronUp, ChevronDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v, digits = 2) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toFixed(digits);
  return String(v);
};

const EdgeIcon = ({ edge, side }) => {
  if (edge === side) return <ChevronUp className="w-3.5 h-3.5 text-emerald-400" />;
  if (edge === null || edge === 'even') return <Minus className="w-3.5 h-3.5 text-slate-500" />;
  return <ChevronDown className="w-3.5 h-3.5 text-rose-400/70" />;
};

const PitcherCard = ({ pitcher, label, side, isFavored }) => {
  const stats = pitcher?.stats || {};
  return (
    <div
      className={`p-4 rounded-lg border ${
        isFavored
          ? 'bg-emerald-500/10 border-emerald-500/40'
          : 'bg-slate-800/40 border-slate-700/50'
      }`}
      data-testid={`pitcher-card-${side}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {isFavored && (
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[10px]">
            <Zap className="w-3 h-3 mr-1" /> Edge
          </Badge>
        )}
      </div>
      <h4 className="text-lg font-bold text-foreground leading-tight" data-testid={`pitcher-name-${side}`}>
        {pitcher?.name || 'TBD'}
      </h4>
      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
        <div className="flex justify-between"><span>Record</span><span className="font-mono text-foreground">{stats.wins ?? 0}-{stats.losses ?? 0}</span></div>
        <div className="flex justify-between"><span>Score</span><span className="font-mono text-foreground">{fmt(pitcher?.score, 1)}/100</span></div>
      </div>
    </div>
  );
};

export default function PitcherMatchupCard({ gameId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const resp = await fetch(`${API_URL}/api/games/${gameId}/pitcher-matchup`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [gameId]);

  if (loading) {
    return (
      <Card data-testid="pitcher-matchup-loading">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data?.available) {
    return (
      <Card data-testid="pitcher-matchup-empty">
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            {data?.message || 'Probable pitchers not yet announced for this game.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { home_pitcher, away_pitcher, edge, score_diff, comparison, home_team, away_team } = data;

  return (
    <Card data-testid="pitcher-matchup-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            Pitchers Matchup
          </span>
          {edge && edge !== 'even' && (
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">
              Edge: {edge === 'home' ? home_team : away_team} (+{fmt(score_diff, 1)})
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pitcher Cards */}
        <div className="grid grid-cols-2 gap-3">
          <PitcherCard pitcher={away_pitcher} label={`Away · ${away_team || ''}`} side="away" isFavored={edge === 'away'} />
          <PitcherCard pitcher={home_pitcher} label={`Home · ${home_team || ''}`} side="home" isFavored={edge === 'home'} />
        </div>

        {/* Stat-by-stat comparison */}
        <div className="rounded-lg border border-slate-700/50 overflow-hidden">
          <div className="grid grid-cols-12 px-3 py-2 bg-slate-800/60 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <div className="col-span-4 text-left">Stat</div>
            <div className="col-span-3 text-center">Away</div>
            <div className="col-span-3 text-center">Home</div>
            <div className="col-span-2 text-right">Hint</div>
          </div>
          {(comparison || []).map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-12 px-3 py-2 text-sm border-t border-slate-700/40 hover:bg-slate-800/30 transition-colors"
              data-testid={`pitcher-stat-${row.label.toLowerCase()}`}
            >
              <div className="col-span-4 font-medium text-foreground">{row.label}</div>
              <div className={`col-span-3 text-center font-mono flex items-center justify-center gap-1 ${
                row.edge === 'away' ? 'text-emerald-400 font-bold' : 'text-foreground'
              }`}>
                {fmt(row.away, 2)}
                {row.edge && <EdgeIcon edge={row.edge} side="away" />}
              </div>
              <div className={`col-span-3 text-center font-mono flex items-center justify-center gap-1 ${
                row.edge === 'home' ? 'text-emerald-400 font-bold' : 'text-foreground'
              }`}>
                {fmt(row.home, 2)}
                {row.edge && <EdgeIcon edge={row.edge} side="home" />}
              </div>
              <div className="col-span-2 text-right text-[10px] text-muted-foreground self-center">
                {row.hint}
              </div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground italic">
          Pitcher Score 0-100 (40% ERA + 30% WHIP + 20% K/9 + 10% HR/9). Stats from MLB Stats API.
        </p>
      </CardContent>
    </Card>
  );
}
